import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  FileText,
  Save,
  ArrowLeft,
  Loader2,
  Database,
  Type,
  Calendar,
  Hash,
  Mail,
  Link as LinkIcon,
  Phone,
  Image,
  Video,
  Music,
  File,
  CheckSquare,
  Sparkles
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { ROUTES } from '@/utils/constants'
import NumberInput from '@/components/form/NumberInput'
import toast from 'react-hot-toast'

const RecordEdit = () => {
  const { dataInputId, recordId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  console.log('RecordEdit params:', { dataInputId, recordId })

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  const {
    data: record,
    isLoading: recordLoading,
    error: recordError
  } = useQuery(
    ['record', dataInputId, recordId],
    () => dataInputsService.getRecordById(dataInputId, recordId),
    {
      onError: (error) => {
        console.error('Error loading record:', error)
      }
    }
  )

  const {
    data: dataInput,
    isLoading: dataInputLoading
  } = useQuery(
    ['dataInput', dataInputId],
    () => dataInputsService.getById(dataInputId),
    {
      onError: (error) => {
        console.error('Error loading data input:', error)
      }
    }
  )

  // Get only active attributes for the form
  const {
    data: activeAttributes,
    isLoading: attributesLoading
  } = useQuery(
    ['activeAttributes', dataInputId],
    () => dataInputsService.getActiveAttributes(dataInputId),
    {
      enabled: !!dataInputId,
      onError: (error) => {
        console.error('Error loading attributes:', error)
      }
    }
  )

  // Populate form when both record and activeAttributes are loaded
  useEffect(() => {
    if (record?.value && activeAttributes) {
      activeAttributes.forEach(attribute => {
        // Skip auto-generated attributes - they cannot be edited
        if (attribute.is_auto_generated) {
          return
        }
        const value = record.value[attribute.name]
        if (value !== undefined) {
          setValue(attribute.name, value)
        }
      })
    }
  }, [record, activeAttributes, setValue])

  // Update record mutation
  const updateRecordMutation = useMutation(
    (recordData) => dataInputsService.updateRecord(dataInputId, recordId, recordData),
    {
      onSuccess: () => {
        toast.success('Record updated successfully!')
        queryClient.invalidateQueries(['record', dataInputId, recordId])
        queryClient.invalidateQueries(['records', dataInputId])
        queryClient.invalidateQueries(['dataInput', dataInputId])
        navigate(`/data-inputs/${dataInputId}?tab=records`)
      },
      onError: (error) => {
        console.error('Error updating record:', error)
      }
    }
  )

  const onSubmit = async (data) => {
    // Build the record value object from form data
    const recordValue = {}

    if (activeAttributes) {
      activeAttributes.forEach(attribute => {
        // Skip auto-generated attributes - they cannot be edited
        if (attribute.is_auto_generated) {
          return
        }
        
        const value = data[attribute.name]
        if (value !== undefined && value !== '') {
          // Convert value based on data type
          switch (attribute.data_type) {
            case 'Number':
              recordValue[attribute.name] = Number(value)
              break
            case 'Decimal':
              recordValue[attribute.name] = parseFloat(value)
              break
            case 'Boolean':
              recordValue[attribute.name] = value === true || value === 'true' || value === 'on'
              break
            case 'Date':
            case 'Datetime':
              recordValue[attribute.name] = value
              break
            default:
              recordValue[attribute.name] = value
          }
        }
      })
    }

    const recordData = {
      value: recordValue
    }

    console.log('Updating record data:', recordData)
    updateRecordMutation.mutate(recordData)
  }

  const getFieldIcon = (dataType) => {
    switch (dataType) {
      case 'Email': return Mail
      case 'URL': return LinkIcon
      case 'Phone Number': return Phone
      case 'Number':
      case 'Decimal': return Hash
      case 'Date':
      case 'Datetime': return Calendar
      case 'Boolean': return CheckSquare
      case 'Image': return Image
      case 'Video': return Video
      case 'Audio': return Music
      case 'File': return File
      default: return Type
    }
  }

  const renderField = (attribute) => {
    const Icon = getFieldIcon(attribute.data_type)
    const fieldName = attribute.name
    const isRequired = attribute.is_required

    const baseValidation = {
      required: isRequired ? `${attribute.label || attribute.name} is required` : false
    }

    // If attribute has allowed_values, show dropdown
    if (attribute.allowed_values && attribute.allowed_values.length > 0) {
      return (
        <select
          {...register(fieldName, baseValidation)}
          className={`input ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
        >
          <option value="">Select an option...</option>
          {attribute.allowed_values.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      )
    }

    // Otherwise, show input based on data type
    switch (attribute.data_type) {
      case 'Email':
        return (
          <input
            {...register(fieldName, {
              ...baseValidation,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            type="email"
            className={`input pl-10 ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
            placeholder={attribute.placeholder || `Enter ${attribute.label || attribute.name}`}
          />
        )

      case 'URL':
        return (
          <input
            {...register(fieldName, {
              ...baseValidation,
              pattern: {
                value: /^https?:\/\/.+/,
                message: 'Invalid URL format'
              }
            })}
            type="url"
            className={`input pl-10 ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
            placeholder={attribute.placeholder || 'https://example.com'}
          />
        )

      case 'Number':
        return (
          <NumberInput
            {...register(fieldName, {
              ...baseValidation,
              validate: (value) => {
                if (!value) return true
                // Remove thousand separators and check if it's a valid number
                const cleanValue = value.replace(/,/g, '')
                const num = parseFloat(cleanValue)
                return !isNaN(num) || 'Must be a valid number'
              }
            })}
            allowDecimals={false}
            className={`input pl-10 ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
            placeholder={attribute.placeholder || 'Enter number (e.g., 1,234)'}
          />
        )

      case 'Decimal':
        return (
          <NumberInput
            {...register(fieldName, {
              ...baseValidation,
              validate: (value) => {
                if (!value) return true
                // Convert US format to standard format for validation
                const cleanValue = value.replace(/,/g, '')
                const num = parseFloat(cleanValue)
                return !isNaN(num) || 'Must be a valid decimal number'
              }
            })}
            allowDecimals={true}
            className={`input pl-10 ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
            placeholder={attribute.placeholder || 'Enter decimal (e.g., 1,234.56)'}
          />
        )

      case 'Date':
        return (
          <input
            {...register(fieldName, baseValidation)}
            type="date"
            className={`input pl-10 ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
          />
        )

      case 'Datetime':
        return (
          <input
            {...register(fieldName, baseValidation)}
            type="datetime-local"
            className={`input pl-10 ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
          />
        )

      case 'Phone Number':
        return (
          <input
            {...register(fieldName, {
              ...baseValidation,
              pattern: {
                value: /^0\d{7,14}$/,
                message: 'Phone number must start with 0 and contain 8-15 digits'
              }
            })}
            type="tel"
            className={`input pl-10 ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
            placeholder="0123456789"
          />
        )

      case 'Boolean':
        return (
          <div className="flex items-center pl-10">
            <input
              {...register(fieldName)}
              type="checkbox"
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              {attribute.label || attribute.name}
            </label>
          </div>
        )

      default:
        return (
          <textarea
            {...register(fieldName, baseValidation)}
            rows={3}
            className={`input pl-10 resize-none ${errors[fieldName] ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
            placeholder={attribute.placeholder || `Enter ${attribute.label || attribute.name}`}
          />
        )
    }
  }

  if (recordLoading || dataInputLoading || attributesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading record details...</span>
        </div>
      </div>
    )
  }

  if (recordError || !record || !dataInput) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <FileText className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Record not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The record you're trying to edit doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate(`/data-inputs/${dataInputId}?tab=records`)}
              className="btn-primary btn-sm"
            >
              Back to Records
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/data-inputs/${dataInputId}?tab=records`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Records
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Record
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Update record data for {dataInput.name}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Record Data</h3>
            <p className="mt-1 text-sm text-gray-600">
              Update the data for this record
            </p>
          </div>
          <div className="p-6 space-y-6">
            {activeAttributes && activeAttributes.length > 0 ? (
              activeAttributes.map((attribute) => {
                // Show auto-generated fields as read-only with their value
                if (attribute.is_auto_generated) {
                  return (
                    <div key={attribute.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <Sparkles className="h-5 w-5 text-gray-500" />
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-800">
                            {attribute.label || attribute.name}
                          </h4>
                          <p className="text-xs text-blue-600 mt-1">
                            Auto-generated field. Current value: <span className="font-mono">{record?.value?.[attribute.name] || 'N/A'}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                }

                const Icon = getFieldIcon(attribute.data_type)
                return (
                  <div key={attribute.id}>
                    <label className="block text-sm font-medium text-gray-700">
                      {attribute.label || attribute.name}
                      {attribute.is_required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {attribute.description && (
                      <p className="mt-1 text-xs text-gray-500">{attribute.description}</p>
                    )}
                    <div className="mt-1 relative">
                      {attribute.data_type !== 'Boolean' && !(attribute.allowed_values && attribute.allowed_values.length > 0) && (
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Icon className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      {renderField(attribute)}
                    </div>
                    {attribute.data_type === 'Phone Number' && (
                      <div className="mt-1 text-xs text-gray-500">
                        Must start with 0, minimum 8 digits (e.g., 0123456789)
                      </div>
                    )}
                    {errors[attribute.name] && (
                      <p className="mt-1 text-sm text-red-600">{errors[attribute.name].message}</p>
                    )}
                  </div>
                )
              })
            ) : (
              <div className="text-center py-8">
                <Database className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No active attributes</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This data input doesn't have any active attributes. Please activate attributes first.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Submit Buttons */}
        {activeAttributes && activeAttributes.length > 0 && (
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/data-inputs/${dataInputId}?tab=records`)}
              className="btn-outline btn-md"
              disabled={updateRecordMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary btn-md flex items-center"
              disabled={updateRecordMutation.isLoading}
            >
              {updateRecordMutation.isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Update Record
                </>
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

export default RecordEdit
